import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtEUR, fmtTRY, useAuth } from "../lib/api";
import { Plus, Pencil, Trash2, X, Check, Anchor, ShoppingBag, Camera, Image as ImageIcon, Percent, Users } from "lucide-react";
import RealizationModal from "../components/RealizationModal";

const emptyDraft = {
  payment_type: "gemi",
  date: "",
  cabin_id: "",
  person_idx: "",
  amount: "",       // Unified amount (₺ or €)
  currency: "EUR",  // "EUR" | "TRY" — Gemi default EUR, others TRY
  amount_eur: "",   // saved on submit
  amount_try: "",   // saved on submit
  description: "",
  receipt_url: "",
  item_key: "",
  for_all: false,
  for_all_payer: "", // "cabinId|personIdx" — for_all olduğunda gerçek ödeyeni tanımlar
  splits: [], // [{person_idx, amount}]
};

export default function Payments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cabins, setCabins] = useState([]);
  const [payments, setPayments] = useState([]);
  const [otherCosts, setOtherCosts] = useState([]);
  const [digerSummary, setDigerSummary] = useState({ target_try: 0, target_eur: 0, paid_try: 0, paid_eur: 0 });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [showNew, setShowNew] = useState(false);
  const [showRealization, setShowRealization] = useState(false);

  const load = async () => {
    try {
      const [c, p, oc, ds] = await Promise.all([
        api.get("/cabins"),
        api.get("/payments"),
        api.get("/other-costs"),
        api.get("/diger-maliyet-summary"),
      ]);
      setCabins(Array.isArray(c.data) ? c.data : []);
      setPayments(Array.isArray(p.data) ? p.data : []);
      setOtherCosts(Array.isArray(oc.data) ? oc.data : []);
      setDigerSummary(ds.data || {});
    } catch (e) { console.error("Payments load:", e); }
  };
  useEffect(() => { load(); }, []);

  // ActualCosts (Muhasebe) sekme çubuğundaki butonlardan tetiklenen events
  useEffect(() => {
    const openNew = () => { startNew(); };
    const openRealization = () => { setShowRealization(true); };
    window.addEventListener("muhasebe:new-payment", openNew);
    window.addEventListener("muhasebe:open-realization", openRealization);
    return () => {
      window.removeEventListener("muhasebe:new-payment", openNew);
      window.removeEventListener("muhasebe:open-realization", openRealization);
    };
     
  }, []);

  const cabinsById = useMemo(() => Object.fromEntries(cabins.map((c) => [c.id, c])), [cabins]);

  // Flatten persons across cabins for picker
  const allPersons = useMemo(() => {
    const list = [];
    for (const c of cabins) {
      (c.people || []).forEach((p, i) => {
        list.push({ cabin_id: c.id, cabin_name: c.name, idx: i, full_name: p.full_name || `Kabin ${c.order}` });
      });
    }
    return list;
  }, [cabins]);

  const startNew = () => {
    setShowNew(true);
    setEditingId(null);
    setDraft({ ...emptyDraft, date: new Date().toISOString().slice(0, 10) });
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setShowNew(false);
    let person_idx = "";
    if (p.person_label) {
      const found = allPersons.find((ap) => ap.full_name === p.person_label && ap.cabin_id === p.cabin_id);
      if (found) person_idx = `${found.cabin_id}|${found.idx}`;
    }
    const ptype = p.payment_type || "gemi";
    // Currency detect from amounts
    const hasEur = Number(p.amount_eur || 0) > 0;
    const hasTry = Number(p.amount_try || 0) > 0;
    let currency = ptype === "gemi" ? "EUR" : "TRY";
    if (hasEur && !hasTry) currency = "EUR";
    else if (hasTry && !hasEur) currency = "TRY";
    const amount = currency === "EUR" ? (p.amount_eur || "") : (p.amount_try || "");
    // Splits: reload group from same split_group_id (front load'ta zaten geliyor)
    const groupSplits = p.split_group_id
      ? (payments || []).filter((x) => x.split_group_id === p.split_group_id).map((x) => {
          const found = allPersons.find((ap) => ap.full_name === x.person_label && ap.cabin_id === x.cabin_id);
          return {
            person_idx: found ? `${found.cabin_id}|${found.idx}` : "",
            amount: currency === "EUR" ? (x.amount_eur || 0) : (x.amount_try || 0),
          };
        })
      : (person_idx ? [{ person_idx, amount: Number(amount) || 0 }] : []);
    setDraft({
      payment_type: ptype,
      date: p.date,
      cabin_id: p.cabin_id,
      person_idx,
      amount,
      currency,
      amount_eur: p.amount_eur || "",
      amount_try: p.amount_try || "",
      description: p.description || "",
      receipt_url: p.receipt_url || "",
      item_key: p.item_key || "",
      for_all: !!p.for_all,
      for_all_payer: (p.for_all && p.person_label) ? (() => {
        const found = allPersons.find((ap) => ap.full_name === p.person_label);
        return found ? `${found.cabin_id}|${found.idx}` : "";
      })() : "",
      splits: groupSplits.length > 0 ? groupSplits : [],
    });
  };

  const cancel = () => { setEditingId(null); setShowNew(false); setDraft(emptyDraft); };

  const save = async () => {
    if (!draft.date) return;
    const total = parseFloat(String(draft.amount).replace(",", ".")) || 0;
    const currency = draft.currency === "EUR" ? "EUR" : "TRY";
    const ptype = draft.payment_type;

    // "Herkes Adına" — diger_maliyet için, ödeme yapan seçimi yok
    if (ptype === "diger_maliyet" && draft.for_all) {
      if (total <= 0) return;
      // Payer attribution: for_all_payer varsa person_label'a yaz → settlement'ta o kabin kredi alır
      let payerLabel = "";
      if (draft.for_all_payer) {
        const [cId, idx] = String(draft.for_all_payer).split("|");
        const c = cabinsById[cId];
        payerLabel = c?.people?.[Number(idx)]?.full_name || "";
      }
      const body = {
        date: draft.date,
        cabin_id: "",
        amount_eur: currency === "EUR" ? total : 0,
        amount_try: currency === "TRY" ? total : 0,
        description: draft.description || "",
        payment_type: ptype,
        person_label: payerLabel,
        receipt_url: draft.receipt_url || "",
        item_key: draft.item_key || "",
        for_all: true,
      };
      if (editingId) await api.put(`/payments/${editingId}`, body);
      else await api.post(`/payments`, body);
      cancel();
      await load();
      return;
    }

    // Ödeme Yapan(lar) — en az 1 kişi
    const payers = (Array.isArray(draft.splits) ? draft.splits : []).filter((s) => s && s.person_idx);
    if (payers.length === 0 || total <= 0) return;

    // Tek ödeyen
    if (payers.length === 1) {
      const s = payers[0];
      const [cId, idx] = String(s.person_idx).split("|");
      const c = cabinsById[cId];
      const personLabel = c?.people?.[Number(idx)]?.full_name || "";
      const body = {
        date: draft.date,
        cabin_id: cId || "",
        amount_eur: currency === "EUR" ? total : 0,
        amount_try: currency === "TRY" ? total : 0,
        description: draft.description || "",
        payment_type: ptype,
        person_label: personLabel,
        receipt_url: draft.receipt_url || "",
        item_key: draft.item_key || "",
        for_all: false,
      };
      if (editingId) await api.put(`/payments/${editingId}`, body);
      else await api.post(`/payments`, body);
      cancel();
      await load();
      return;
    }

    // Çoklu ödeyen — splits[]
    const body = {
      date: draft.date,
      cabin_id: "",
      amount_eur: currency === "EUR" ? total : 0,
      amount_try: currency === "TRY" ? total : 0,
      description: draft.description || "",
      payment_type: ptype,
      person_label: "",
      receipt_url: draft.receipt_url || "",
      item_key: draft.item_key || "",
      for_all: false,
      splits: payers.map((s) => {
        const [cId, idx] = String(s.person_idx).split("|");
        const c = cabinsById[cId];
        const pl = c?.people?.[Number(idx)]?.full_name || "";
        const amt = parseFloat(String(s.amount || 0).toString().replace(",", ".")) || 0;
        return {
          cabin_id: cId || "",
          person_label: pl,
          amount_eur: currency === "EUR" ? amt : 0,
          amount_try: currency === "TRY" ? amt : 0,
        };
      }),
    };
    if (editingId) await api.put(`/payments/${editingId}`, body);
    else await api.post(`/payments`, body);
    cancel();
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Bu ödeme silinsin mi?")) return;
    await api.delete(`/payments/${id}`);
    await load();
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const totalGemiEur = useMemo(() => payments.filter((p) => (p.payment_type || "gemi") === "gemi").reduce((s, p) => s + (p.amount_eur || 0), 0), [payments]);
  const totalAlisverisTry = useMemo(() => payments.filter((p) => p.payment_type === "alisveris").reduce((s, p) => s + (p.amount_try || 0), 0), [payments]);

  return (
    <div className="space-y-5">
      {/* Header çubuğu artık ActualCosts (Muhasebe) sekme satırından yönetiliyor.
          Ödemeler başlığı ve butonlar burada gösterilmez. */}

      <div className="grid grid-cols-3 gap-3">
        <div className="tk-card p-4" data-testid="payments-total-gemi-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] md:text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Toplam Tekne Ödemesi</div>
            <Anchor className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="text-xl md:text-2xl font-display font-semibold" data-testid="payments-total-gemi">{fmtEUR(totalGemiEur)}</div>
        </div>
        <div className="tk-card p-4" data-testid="payments-total-diger-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] md:text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Tekne Diğer Maliyetler</div>
            <Anchor className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="text-xl md:text-2xl font-display font-semibold" data-testid="payments-total-diger">{fmtTRY(digerSummary.paid_try)}</div>
          <div className="text-[10px] mt-0.5" style={{ color: "hsl(var(--text-2))" }}>Hedef: {fmtTRY(digerSummary.target_try)}</div>
        </div>
        <div className="tk-card p-4" data-testid="payments-total-alisveris-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] md:text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Toplam Alışveriş Harcaması</div>
            <ShoppingBag className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="text-xl md:text-2xl font-display font-semibold" data-testid="payments-total-alisveris">{fmtTRY(totalAlisverisTry)}</div>
        </div>
      </div>

      {isAdmin && showNew && (
        <div className="tk-card p-4" data-testid="new-payment-card">
          <PaymentForm draft={draft} setDraft={setDraft} cabins={cabins} allPersons={allPersons} otherCosts={otherCosts} onSave={save} onCancel={cancel} />
        </div>
      )}

      <div className="space-y-2">
        {payments.length === 0 && (
          <div className="tk-card p-6 text-center text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Henüz ödeme girilmedi.
          </div>
        )}
        {payments.map((p) => {
          const isEditing = isAdmin && editingId === p.id;
          const cabin = cabinsById[p.cabin_id];
          const isShop = p.payment_type === "alisveris";
          if (isEditing) {
            return (
              <div key={p.id} className="tk-card p-4" data-testid={`edit-payment-${p.id}`}>
                <PaymentForm draft={draft} setDraft={setDraft} cabins={cabins} allPersons={allPersons} otherCosts={otherCosts} onSave={save} onCancel={cancel} />
              </div>
            );
          }
          return (
            <div key={p.id} data-testid={`payment-row-${p.id}`} className="tk-card p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                {isShop ? <ShoppingBag className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} /> : <Anchor className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-display font-semibold">{cabin?.name || "—"}</div>
                  <span className="tk-chip">{isShop ? "Alışveriş" : "Tekne"}</span>
                  <span className="tk-chip">{fmtDate(p.date)}</span>
                </div>
                {p.person_label && (
                  <div className="mt-0.5 text-xs" style={{ color: "hsl(var(--text-2))" }}>
                    Kişi: <b className="text-[hsl(var(--text-1))]">{p.person_label}</b>
                  </div>
                )}
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {p.amount_eur > 0 && (
                    <span><span className="text-xs uppercase tracking-wider" style={{ color: "hsl(var(--text-2))" }}>EUR </span><b>{fmtEUR(p.amount_eur)}</b></span>
                  )}
                  {p.amount_try > 0 && (
                    <span><span className="text-xs uppercase tracking-wider" style={{ color: "hsl(var(--text-2))" }}>TRY </span><b>{fmtTRY(p.amount_try)}</b></span>
                  )}
                </div>
                {p.description && (
                  <div className="mt-1 text-xs" style={{ color: "hsl(var(--text-2))" }}>{p.description}</div>
                )}
                {p.receipt_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${p.receipt_url}`} target="_blank" rel="noopener noreferrer" data-testid={`receipt-link-${p.id}`} className="mt-1 inline-flex items-center gap-1 text-xs" style={{ color: "hsl(var(--primary))" }}>
                    <Camera className="w-3 h-3" /> Fişi Gör
                  </a>
                )}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <button data-testid={`edit-payment-btn-${p.id}`} onClick={() => startEdit(p)} className="tk-btn-ghost !py-2 !px-2.5"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-payment-btn-${p.id}`} onClick={() => remove(p.id)} className="tk-btn-danger !py-2 !px-2.5"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <RealizationModal
        open={showRealization}
        onClose={() => setShowRealization(false)}
        onSaved={() => { /* dashboard read on next mount */ }}
        scope="diger"
      />
    </div>
  );
}

function PaymentForm({ draft, setDraft, cabins, allPersons, otherCosts, onSave, onCancel }) {
  const upd = (k, v) => setDraft({ ...draft, [k]: v });
  const isShop = draft.payment_type === "alisveris";
  const isDiger = draft.payment_type === "diger_maliyet";
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const fileRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  // Payment type değişince currency'yi zorla ayarla (Gemi=EUR, Alışveriş=TRY, Diğer=TRY default)
  useEffect(() => {
    if (draft.payment_type === "gemi") {
      if (draft.currency !== "EUR") setDraft((d) => ({ ...d, currency: "EUR" }));
    } else if (draft.payment_type === "alisveris" || draft.payment_type === "diger_maliyet") {
      if (draft.currency !== "TRY") setDraft((d) => ({ ...d, currency: "TRY" }));
    }
     
  }, [draft.payment_type]);

  // İlk açılışta ödeyen satırı auto-add artık PayersSection içinde yapılır — burada gerek yok.

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/payments/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("receipt_url", data.url);
    } catch (e2) {
      alert(e2.response?.data?.detail || "Fiş yüklenemedi");
    } finally {
      setUploadingReceipt(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeReceipt = () => upd("receipt_url", "");
  const isPdf = (draft.receipt_url || "").toLowerCase().endsWith(".pdf");

  // Currency locked for Gemi (must be EUR) and Alışveriş (must be TRY); free for Diger
  const currencyLocked = draft.payment_type === "gemi" || draft.payment_type === "alisveris";

  return (
    <div className="space-y-3">
      {/* Type toggle — 2 sekme (Alışveriş kendi kulakçığında) */}
      <div className="grid grid-cols-2 gap-2 p-1 rounded-xl" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
        <button type="button" data-testid="ptype-gemi-btn" onClick={() => upd("payment_type", "gemi")} className={`text-sm font-medium px-3 py-2 rounded-lg flex items-center justify-center gap-1.5 transition ${draft.payment_type === "gemi" ? "bg-white shadow-sm" : ""}`} style={{ color: draft.payment_type === "gemi" ? "hsl(var(--text-1))" : "hsl(var(--text-2))" }}>
          <Anchor className="w-4 h-4" /> Tekne
        </button>
        <button type="button" data-testid="ptype-diger-btn" onClick={() => upd("payment_type", "diger_maliyet")} className={`text-sm font-medium px-3 py-2 rounded-lg flex items-center justify-center gap-1.5 transition ${draft.payment_type === "diger_maliyet" ? "bg-white shadow-sm" : ""}`} style={{ color: draft.payment_type === "diger_maliyet" ? "hsl(var(--text-1))" : "hsl(var(--text-2))" }}>
          <Anchor className="w-4 h-4" /> Tekne Diğer
        </button>
      </div>

      {/* Row 1 · Tarih | Tutar | Döviz | Açıklama */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-3">
          <label className="tk-label">Tarih</label>
          <input data-testid="payment-date-input" type="date" className="tk-input" value={draft.date} onChange={(e) => upd("date", e.target.value)} />
        </div>
        <div className="col-span-6 md:col-span-3">
          <label className="tk-label">Tutar <span style={{ color: "hsl(var(--error))" }}>*</span></label>
          <input data-testid="payment-amount-input" type="text" inputMode="decimal" className="tk-input" value={draft.amount} onChange={(e) => upd("amount", e.target.value)} placeholder="0" />
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="tk-label">Döviz</label>
          <select
            data-testid="payment-currency-select"
            className="tk-input"
            value={draft.currency || (draft.payment_type === "gemi" ? "EUR" : "TRY")}
            onChange={(e) => upd("currency", e.target.value)}
            disabled={currencyLocked}
            title={currencyLocked ? "Bu ödeme tipinde döviz sabittir" : ""}
          >
            <option value="TRY">₺ TRY</option>
            <option value="EUR">€ EUR</option>
          </select>
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="tk-label">Açıklama</label>
          <input data-testid="payment-desc-input" type="text" className="tk-input" value={draft.description} onChange={(e) => upd("description", e.target.value)} placeholder={isShop ? "örn. Migros - kasap" : "örn. İşbankası'na havale"} />
        </div>

        {/* Tekne Diğer için: Kalem + Herkes Adına */}
        {isDiger && (
          <>
            <div className="col-span-12 md:col-span-6">
              <label className="tk-label">Kalem <span style={{ color: "hsl(var(--error))" }}>*</span></label>
              <select data-testid="payment-item-select" className="tk-input" value={draft.item_key || ""} onChange={(e) => upd("item_key", e.target.value)}>
                <option value="">— kalem seçiniz —</option>
                {(otherCosts || []).map((it) => (
                  <option key={it.id} value={it.name}>{it.name} · {it.currency === "EUR" ? "€" : "₺"} {it.unit_cost}</option>
                ))}
              </select>
            </div>
            <div className="col-span-12 md:col-span-6 flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="for-all-check"
                data-testid="payment-forall-check"
                checked={!!draft.for_all}
                onChange={(e) => upd("for_all", e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="for-all-check" className="text-sm cursor-pointer">
                <b>Herkes Adına (Tekne Diğer)</b>
                <span className="block text-[11px]" style={{ color: "hsl(var(--text-2))" }}>Seçilirse ödeme kabinlere kişi sayısına göre paylaştırılır.</span>
              </label>
            </div>
            {draft.for_all && (
              <div className="col-span-12">
                <label className="tk-label">Herkes Adına Ödeyen <span style={{ color: "hsl(var(--text-2))" }}>(opsiyonel — settlement&apos;ta bu kabin tam kredi alır)</span></label>
                <select
                  data-testid="payment-forall-payer-select"
                  className="tk-input"
                  value={draft.for_all_payer || ""}
                  onChange={(e) => upd("for_all_payer", e.target.value)}
                >
                  <option value="">— ödeyen belirtme (nötr dağıt) —</option>
                  {allPersons.map((p) => (
                    <option key={`${p.cabin_id}|${p.idx}`} value={`${p.cabin_id}|${p.idx}`}>
                      {p.full_name} · {p.cabin_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Ödeme Yapan(lar) — diger_maliyet + for_all değilse görünür */}
        {!(isDiger && draft.for_all) && (
          <div className="col-span-12">
            <PayersSection draft={draft} setDraft={setDraft} allPersons={allPersons} />
          </div>
        )}

        {/* Fiş yükleme — alışveriş için */}
        {isShop && (
          <div className="col-span-12">
            <label className="tk-label">Fiş / Fatura (foto veya PDF)</label>
            {draft.receipt_url ? (
              <div className="flex items-center gap-3">
                <a href={`${backend}${draft.receipt_url}`} target="_blank" rel="noopener noreferrer" data-testid="receipt-preview-link" className="block">
                  {isPdf ? (
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                      <ImageIcon className="w-6 h-6" style={{ color: "hsl(var(--text-2))" }} />
                    </div>
                  ) : (
                    <img src={`${backend}${draft.receipt_url}`} alt="Fiş" className="w-16 h-16 rounded-xl object-cover border" style={{ borderColor: "hsl(var(--border))" }} />
                  )}
                </a>
                <div className="flex flex-col gap-1">
                  <span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>{isPdf ? "PDF eklendi" : "Görsel eklendi"}</span>
                  <button type="button" data-testid="receipt-remove-btn" onClick={removeReceipt} className="tk-btn-ghost !py-1.5 !px-2 text-xs flex items-center gap-1 self-start"><Trash2 className="w-3 h-3" /> Kaldır</button>
                </div>
              </div>
            ) : (
              <label className="tk-btn-ghost inline-flex items-center gap-1.5 cursor-pointer !py-2 !px-3 text-sm">
                <Camera className="w-4 h-4" /> {uploadingReceipt ? "Yükleniyor..." : "Fiş Ekle"}
                <input ref={fileRef} data-testid="receipt-upload-input" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" disabled={uploadingReceipt} />
              </label>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 justify-end pt-1">
        <button data-testid="payment-cancel-btn" onClick={onCancel} className="tk-btn-ghost flex items-center gap-1.5"><X className="w-4 h-4" /> Vazgeç</button>
        <button data-testid="payment-save-btn" onClick={onSave} className="tk-btn flex items-center gap-1.5"><Check className="w-4 h-4" /> Kaydet</button>
      </div>
    </div>
  );
}


/**
 * Ödeme Yapan(lar) — unified section.
 * En az 1 kişi zorunlu (form validation). İlk satır otomatik total'i alır.
 * 2+ satır varsa splits[] olarak backend'e gider.
 * Ara satır tutarı değişirse kalan son satıra kayar.
 */
export function PayersSection({ draft, setDraft, allPersons }) {
  const total = parseFloat(String(draft.amount).replace(",", ".")) || 0;
  const currency = draft.currency === "EUR" ? "EUR" : "TRY";
  const payers = Array.isArray(draft.splits) ? draft.splits : [];

  const setPayers = (next) => setDraft({ ...draft, splits: next });

  // Auto-init & sync: total > 0 iken
  //   - hiç payer yoksa → auto-add 1 satır (kişi boş, tutar=total, _auto flag)
  //   - tek payer var ve _auto flag'i hâlâ set ise → tutarı total ile senkronize et
  //   - 2+ payer varsa → son satırı auto olarak "kalan"a senkronize et
  useEffect(() => {
    if (total <= 0) return;
    if (payers.length === 0) {
      setPayers([{ person_idx: "", amount: total, _auto: true }]);
      return;
    }
    if (payers.length === 1 && payers[0]?._auto) {
      const cur = parseFloat(String(payers[0].amount).replace(",", ".")) || 0;
      if (Math.abs(cur - total) > 0.001) {
        setPayers([{ ...payers[0], amount: total }]);
      }
      return;
    }
    if (payers.length >= 2) {
      const sumExceptLast = payers.slice(0, -1).reduce((s, x) => s + (parseFloat(String(x.amount).replace(",", ".")) || 0), 0);
      const rest = Math.max(0, total - sumExceptLast);
      const curLast = parseFloat(String(payers[payers.length - 1].amount).replace(",", ".")) || 0;
      if (Math.abs(curLast - rest) > 0.001) {
        const next = [...payers];
        next[next.length - 1] = { ...next[next.length - 1], amount: rest };
        setPayers(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, payers.length]);

  const addPayer = () => {
    if (payers.length === 0) {
      setPayers([{ person_idx: "", amount: total > 0 ? total : "" }]);
      return;
    }
    const existSum = payers.reduce((s, x) => s + (parseFloat(String(x.amount).replace(",", ".")) || 0), 0);
    const remaining = total > 0 ? Math.max(0, total - existSum) : 0;
    // Yeni satır eklenince mevcut ilk satır artık user-managed → _auto flag'ini temizle
    const cleaned = payers.map((p) => ({ ...p, _auto: false }));
    setPayers([...cleaned, { person_idx: "", amount: remaining }]);
  };

  const removePayer = (idx) => {
    if (payers.length <= 1) return;
    setPayers(payers.filter((_, i) => i !== idx));
  };

  const updPerson = (idx, val) => setPayers(payers.map((s, i) => (i === idx ? { ...s, person_idx: val } : s)));

  const updAmount = (idx, val) => {
    // Kullanıcı elle tutarı değiştirdi → o satırın _auto flag'ini kaldır (single-row auto sync durur)
    const next = payers.map((s, i) => (i === idx ? { ...s, amount: val, _auto: false } : s));
    if (next.length >= 2 && idx !== next.length - 1) {
      const sumExceptLast = next.slice(0, -1).reduce((s, x) => s + (parseFloat(String(x.amount).replace(",", ".")) || 0), 0);
      const rest = Math.max(0, total - sumExceptLast);
      next[next.length - 1] = { ...next[next.length - 1], amount: rest };
    }
    setPayers(next);
  };

  const sum = payers.reduce((s, x) => s + (parseFloat(String(x.amount).replace(",", ".")) || 0), 0);
  const diff = total - sum;
  const fmt = (v) => (currency === "EUR"
    ? `€${Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`
    : `${Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ₺`);

  return (
    <div className="rounded-xl border p-3" style={{ borderColor: "hsl(var(--border))", backgroundColor: "hsl(var(--surface-2))" }} data-testid="payers-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5" style={{ color: "hsl(var(--text-2))" }}>
          <Users className="w-3.5 h-3.5" /> Ödeme Yapan(lar)
        </div>
        <button
          type="button"
          data-testid="add-payer-btn"
          onClick={addPayer}
          className="tk-btn-ghost !py-1 !px-2 text-xs flex items-center gap-1"
          title={total <= 0 ? "Tutar girmeden de ekleyebilirsin — sonra doldurursun" : "Ödeyen ekle"}
        >
          <Plus className="w-3 h-3" /> Kişi Ekle
        </button>
      </div>
      {payers.length === 0 && (
        <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>
          &quot;Kişi Ekle&quot; butonu ile ödeyen(ler)i seçin. Tutar girildiğinde otomatik dağıtılır.
        </div>
      )}
      {payers.length > 0 && (
        <div className="space-y-2">
          {payers.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`payer-row-${i}`}>
              <select
                data-testid={`payer-person-${i}`}
                className="tk-input col-span-7 !py-1.5 text-sm"
                value={s.person_idx || ""}
                onChange={(e) => updPerson(i, e.target.value)}
              >
                <option value="">— kişi seçiniz —</option>
                {allPersons.map((p) => (
                  <option key={`${p.cabin_id}|${p.idx}`} value={`${p.cabin_id}|${p.idx}`}>{p.full_name} · {p.cabin_name}</option>
                ))}
              </select>
              <input
                data-testid={`payer-amount-${i}`}
                className="tk-input col-span-4 !py-1.5 text-sm"
                inputMode="decimal"
                value={s.amount ?? ""}
                onChange={(e) => updAmount(i, e.target.value)}
                placeholder={currency === "EUR" ? "€" : "₺"}
              />
              <button
                type="button"
                data-testid={`payer-remove-${i}`}
                onClick={() => removePayer(i)}
                disabled={payers.length <= 1}
                className="tk-btn-danger !py-1 !px-2 col-span-1 disabled:opacity-40"
                title={payers.length <= 1 ? "En az 1 ödeyen olmalı" : "Kaldır"}
              ><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {payers.length >= 2 && (
            <div className="flex items-center justify-between text-[11px] pt-1 tk-divider">
              <span style={{ color: "hsl(var(--text-2))" }}>Toplam ödeyenler: <b style={{ color: "hsl(var(--text-1))" }}>{fmt(sum)}</b></span>
              <span style={{ color: Math.abs(diff) < 0.01 ? "hsl(var(--success))" : "hsl(var(--error))" }}>
                Fark: {fmt(diff)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
